// hooks/useLCPOptimization.js
import { useEffect, useRef } from 'react';

/**
 * Hook for optimizing Largest Contentful Paint (LCP)
 */
export function useLCPOptimization(brands = []) {
  const preloadedImages = useRef(new Set());

  useEffect(() => {
    if (brands.length === 0) return;

    // Preload the first 4 brand images immediately for LCP optimization
    const priorityBrands = brands.slice(0, 4);
    
    priorityBrands.forEach((brand, index) => {
      if (brand.coverImage && !preloadedImages.current.has(brand.coverImage)) {
        // Create preload link
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = brand.coverImage;
        
        // Set fetchpriority for the first image (LCP candidate)
        if (index === 0) {
          link.fetchPriority = 'high';
        }
        
        document.head.appendChild(link);
        preloadedImages.current.add(brand.coverImage);
        
        console.log(`Preloaded priority image ${index + 1}:`, brand.coverImage);
      }
    });

    // Cleanup function to remove preload links when component unmounts
    return () => {
      const links = document.querySelectorAll('link[rel="preload"][as="image"]');
      links.forEach(link => {
        if (preloadedImages.current.has(link.href)) {
          document.head.removeChild(link);
        }
      });
    };
  }, [brands]);

  return {
    preloadedCount: preloadedImages.current.size
  };
}

/**
 * Hook for measuring and optimizing LCP
 */
export function useLCPMeasurement() {
  const lcpValue = useRef(null);
  const lcpElement = useRef(null);

  useEffect(() => {
    if (!('PerformanceObserver' in window)) return;

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      
      lcpValue.current = lastEntry.startTime;
      lcpElement.current = lastEntry.element;
      
      console.log('LCP measured:', {
        value: lastEntry.startTime,
        element: lastEntry.element?.tagName,
        url: lastEntry.url
      });

      // Log warning if LCP is poor
      if (lastEntry.startTime > 2500) {
        console.warn(`Poor LCP: ${lastEntry.startTime}ms (should be < 2500ms)`);
        console.warn('LCP element:', lastEntry.element);
      }
    });

    try {
      observer.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (error) {
      console.warn('LCP observation not supported:', error);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return {
    lcpValue: lcpValue.current,
    lcpElement: lcpElement.current
  };
}

/**
 * Hook for critical resource hints
 */
export function useCriticalResourceHints(resources = []) {
  useEffect(() => {
    resources.forEach(resource => {
      const { href, as, type = 'preload', crossorigin, fetchpriority } = resource;
      
      // Check if already exists
      const existing = document.querySelector(`link[href="${href}"]`);
      if (existing) return;

      const link = document.createElement('link');
      link.rel = type;
      link.href = href;
      
      if (as) link.as = as;
      if (crossorigin) link.crossOrigin = crossorigin;
      if (fetchpriority) link.fetchPriority = fetchpriority;
      
      document.head.appendChild(link);
    });
  }, [resources]);
}

export default useLCPOptimization;